import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "onClick", "type", "isLoaded", "showTooltip", "size", "childMarginInlineStart", "childTruncateLength"];
import { useEffect, useMemo, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { AVATAR_SIZES, DEFAULT_PROPS } from '../Constants';
import { useParsedColorScheme } from './hooks/useParsedColorScheme';
import { getAvatarContainerProps, getAvatarContentProps, getAvatarContentWrapperProps } from '../lib/displayUtils';
import { AVATAR_SOURCES } from '../lib/AvatarSources';
import { createImageObject } from '../lib/createImageObject';
import { delayUntilIdle } from '../lib/delayUntilIdle';
import { getDefaultAvatarType } from '../lib/getDefaultAvatarType';
import { AvatarContainer } from './components/AvatarContainer';
import { AvatarContent } from './components/AvatarContent';
import { AvatarContentWrapper } from './components/AvatarContentWrapper';
import { Caption } from './components/Caption';
import { BaseContainer } from './defaultSvgIcons/BaseContainer';
import { getSocialBadgeSize, shouldShowSocialBadge, SOCIAL_BADGE_GAP } from './components/SocialBadge';
import styled from 'foundations-theming/utils';

// Reserve room to the right for the social badge, which is absolutely
// positioned and overflows the avatar. Only used when a badge actually renders,
// and sized to match the responsive badge so the reserved space tracks it.
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SocialAvatarWrapper = styled.span.withConfig({
  displayName: "AvatarDisplay__SocialAvatarWrapper"
})(["display:inline-flex;padding-inline-end:", ";"], ({
  $reserve
}) => `${$reserve}px`);
export const AvatarDisplay = _ref => {
  var _rest$src;
  let {
      children,
      onClick,
      type,
      isLoaded = true,
      showTooltip = DEFAULT_PROPS.showTooltip,
      size = DEFAULT_PROPS.size,
      childMarginInlineStart = DEFAULT_PROPS.childMarginInlineStart,
      childTruncateLength = DEFAULT_PROPS.childTruncateLength
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const propsWithDefaults = Object.assign({
    showTooltip,
    size,
    childMarginInlineStart,
    childTruncateLength
  }, rest);
  const [isTooltipLoaded, setIsTooltipLoaded] = useState(false);
  const [hubSpotUserAvatarSrc, setHubSpotUserAvatarSrc] = useState(undefined);
  const [isHubspotUserAvatarLoaded, setIsHubspotUserAvatarLoaded] = useState(false);
  const isHubspotUserAvatar = !!(rest.source === AVATAR_SOURCES.HUBSETTINGS_HUBSPOT_USER && (_rest$src = rest.src) !== null && _rest$src !== void 0 && _rest$src.includes('/transparent-default/'));
  useEffect(() => {
    const clear = delayUntilIdle(() => setIsTooltipLoaded(true));
    return () => clear();
  }, []);
  useEffect(() => {
    let isMounted = true;

    // Always reset the hubSpotUserAvatarSrc to undefined when src changes.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHubSpotUserAvatarSrc(undefined);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsHubspotUserAvatarLoaded(false);

    // This is a hack to handle the case where the avatar is a HubSpot user avatar
    // and the src is a default avatar.
    if (isHubspotUserAvatar && rest.src) {
      const img = createImageObject();
      img.onload = function () {
        if (!isMounted) return;
        setIsHubspotUserAvatarLoaded(true);
        if (img.width !== 1) {
          setHubSpotUserAvatarSrc(rest.src);
        }
      };
      img.onerror = function () {
        if (!isMounted) return;
        setIsHubspotUserAvatarLoaded(true);
      };
      img.src = rest.src;
    }
    return () => {
      isMounted = false;
    };
  }, [rest.src, isHubspotUserAvatar]);
  const iconType = useMemo(() => getDefaultAvatarType(type), [type]);
  const {
    parsedBackgroundColor,
    parsedIconColor
  } = useParsedColorScheme(iconType);
  const passedProps = Object.assign({}, propsWithDefaults, {
    src: isHubspotUserAvatar ? hubSpotUserAvatarSrc : rest.src,
    backgroundColor: parsedBackgroundColor,
    iconColor: parsedIconColor,
    onClick,
    type,
    iconType
  });
  const avatarHoverProps = getAvatarContainerProps(passedProps, {
    isTooltipLoaded
  });
  const avatarContentWrapperProps = getAvatarContentWrapperProps(passedProps);
  const avatarContentProps = getAvatarContentProps(passedProps);

  // Compute the badge size once here so the reserved trailing space and the
  // badge itself derive from a single value; `undefined` means no badge.
  const {
    socialNetwork
  } = avatarContentWrapperProps;
  const avatarPx = AVATAR_SIZES[size];
  const socialBadgeSize = shouldShowSocialBadge(socialNetwork, size) && typeof avatarPx === 'number' ? getSocialBadgeSize(socialNetwork, avatarPx) : undefined;
  const containerDataTestIds = {
    'data-test-id': passedProps['data-test-id'] || 'AvatarDisplay-avatarContent',
    'data-avatar-loaded': isHubspotUserAvatar ? isHubspotUserAvatarLoaded : isLoaded
  };
  const avatarContainer = /*#__PURE__*/_jsx(AvatarContainer, Object.assign({}, avatarHoverProps, {
    children: /*#__PURE__*/_jsx(AvatarContentWrapper, Object.assign({}, avatarContentWrapperProps, {
      badgeSize: socialBadgeSize,
      children: /*#__PURE__*/_jsx(BaseContainer, Object.assign({}, containerDataTestIds, {
        children: /*#__PURE__*/_jsx(AvatarContent, Object.assign({}, avatarContentProps))
      }))
    }))
  }));

  // Only wrap (and reserve trailing space) when a badge actually renders.
  // Otherwise render the avatar exactly as before, so responsive avatars and
  // logo-less networks keep their original layout and size.
  const avatarContent = socialBadgeSize !== undefined ? /*#__PURE__*/_jsx(SocialAvatarWrapper, {
    "data-test-id": "AvatarDisplay-social-wrapper",
    $reserve: socialBadgeSize + SOCIAL_BADGE_GAP,
    children: avatarContainer
  }) : avatarContainer;
  if (children) {
    return /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      children: [avatarContent, /*#__PURE__*/_jsx(Caption, {
        childMarginInlineStart: passedProps.childMarginInlineStart,
        childTruncateLength: passedProps.childTruncateLength,
        onClick: onClick,
        children: children
      })]
    });
  }
  return avatarContent;
};
AvatarDisplay.displayName = 'AvatarDisplay';